
export default function MethodologySection() {
  const methodologies = [
    {
      title: "Aprendizaje Activo",
      description: "Metodologías participativas que involucran al estudiante como protagonista de su aprendizaje",
      icon: "ri-lightbulb-line",
      color: "blue"
    },
    {
      title: "Investigación Formativa",
      description: "Desarrollo de proyectos de investigación desde los primeros semestres",
      icon: "ri-search-line",
      color: "green"
    },
    {
      title: "Trabajo Colaborativo",
      description: "Proyectos interdisciplinarios que fomentan el trabajo en equipo",
      icon: "ri-team-line",
      color: "purple"
    },
    {
      title: "Perspectiva Global",
      description: "Enfoque internacional y multicultural en todos los programas",
      icon: "ri-global-line",
      color: "orange"
    },
    {
      title: "Pensamiento Crítico",
      description: "Desarrollo de habilidades analíticas y de evaluación crítica",
      icon: "ri-brain-line",
      color: "red"
    },
    {
      title: "Innovación Pedagógica",
      description: "Uso de tecnologías educativas y metodologías innovadoras",
      icon: "ri-rocket-line",
      color: "indigo"
    }
  ];

  const colorClasses = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    purple: "bg-purple-100 text-purple-600",
    orange: "bg-orange-100 text-orange-600",
    red: "bg-red-100 text-red-600",
    indigo: "bg-indigo-100 text-indigo-600"
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Metodología Innovadora
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nuestros programas se caracterizan por una metodología que combina la excelencia académica 
              con el desarrollo de habilidades transversales fundamentales para el siglo XXI
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {methodologies.map((methodology, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-16 h-16 ${colorClasses[methodology.color as keyof typeof colorClasses]} rounded-2xl flex items-center justify-center mb-6`}>
                  <i className={`${methodology.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {methodology.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {methodology.description}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=University%20students%20in%20a%20modern%20classroom%20engaged%20in%20collaborative%20learning%2C%20diverse%20group%20working%20on%20projects%2C%20interactive%20whiteboard%2C%20natural%20lighting%2C%20contemporary%20educational%20environment%2C%20books%20and%20technology%2C%20academic%20discussion%2C%20Chilean%20university%20setting%2C%20professional%20atmosphere&width=600&height=400&seq=students-learning&orientation=landscape"
                  alt="Estudiantes en programa de bachillerato"
                  className="rounded-2xl shadow-lg object-cover w-full h-80"
                />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Formación Integral
                </h3>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Los programas de bachillerato de RENABACH se enfocan en formar profesionales 
                  integrales, capaces de enfrentar los desafíos del mundo contemporáneo con 
                  una sólida base académica y valores éticos.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-star-line text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Desarrollo Personal</h4>
                      <p className="text-gray-600 text-sm">Fortalecimiento de habilidades blandas y liderazgo</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-medal-line text-green-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Excelencia Académica</h4>
                      <p className="text-gray-600 text-sm">Estándares de calidad reconocidos internacionalmente</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-heart-line text-purple-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Compromiso Social</h4>
                      <p className="text-gray-600 text-sm">Formación en responsabilidad social y ciudadanía</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-compass-line text-orange-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Orientación Vocacional</h4>
                      <p className="text-gray-600 text-sm">Acompañamiento en la elección de carrera profesional</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
